/*
 * Copyright 2020 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     https://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

/**
 * External dependencies
 */
import PropTypes from 'prop-types';
import { useCallback } from '@web-stories-wp/react';
import { __ } from '@web-stories-wp/i18n';
import { v4 as uuidv4 } from 'uuid';
import styled from 'styled-components';
import { Text, THEME_CONSTANTS, Toggle } from '@web-stories-wp/design-system';

/**
 * Internal dependencies
 */
import { Row } from '../../../form';
import { SimplePanel } from '../../panel';
import { getCommonValue } from '../../shared';

const Space = styled.div`
  flex: 0 0 12px;
`;

function VideoOptionsPanel({ selectedElements, pushUpdate }) {
  const resource = getCommonValue(selectedElements, 'resource');
  const { isMuted } = resource;

  const checkboxId = `cb-${uuidv4()}`;

  const handleUpdateIsMuted = useCallback(
    (newMutedValue) =>
      pushUpdate((element) => {
        return {
          resource: {
            ...element.resource,
            isMuted: newMutedValue,
          },
        };
      }, true),

    [pushUpdate]
  );

  return (
    <SimplePanel name="videoOptions" title={__('Audio', 'web-stories')}>
      <Row spaceBetween={false}>
        <label htmlFor={checkboxId}>
          <Text as="span" size={THEME_CONSTANTS.TYPOGRAPHY.PRESET_SIZES.SMALL}>
            {__('Mute Audio', 'web-stories')}
          </Text>
        </label>
        <Space />
        <Toggle
          id={checkboxId}
          checked={isMuted}
          onChange={(evt) => {
            handleUpdateIsMuted(evt.target.checked);
          }}
        />
      </Row>
    </SimplePanel>
  );
}

VideoOptionsPanel.propTypes = {
  selectedElements: PropTypes.array.isRequired,
  pushUpdate: PropTypes.func.isRequired,
};

export default VideoOptionsPanel;
